// src/stores/customerWorkflowsStore.js
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { db } from '@/services/firebaseconfig'
import { doc, getDoc, collection, getDocs } from 'firebase/firestore'
import { onSnapshot } from '@/services/sessionListeners'
import { findTaskStateEntry } from '@/utils/taskUtils'
import { latestReadWins } from '@/utils/latestReadWins'

export const useCustomerWorkflowsStore = defineStore('customerWorkflows', () => {
  const workflowsByCustomerId = ref({})
  const isLoading = ref(false)
  const loadedIds = ref(new Set())
  // True only after loadAll() has fetched the FULL collection this session.
  const allLoaded = ref(false)

  // An older read of a workflow never replaces a newer one (src/utils/latestReadWins.js; bug oqWjAMbYK8Q6n4CjA3Tu).
  const reads = latestReadWins()

  /**
   * Load customerWorkflows for a batch of customer IDs.
   * Skips already-loaded IDs unless force=true.
   */
  async function loadForCustomers(customerIds, force = false) {
    const idsToLoad = force ? customerIds : customerIds.filter(id => !loadedIds.value.has(id))

    if (idsToLoad.length === 0) return

    isLoading.value = true
    const sequence = reads.start()
    try {
      const results = await Promise.all(
        idsToLoad.map(id => getDoc(doc(db, 'customerWorkflows', id)))
      )

      const updated = { ...workflowsByCustomerId.value }
      results.forEach(snap => {
        if (snap.exists() && reads.lands(snap.id, sequence)) {
          const data = snap.data()
          updated[snap.id] = { id: snap.id, ...data }
          // Only cache as "loaded" if backend has processed this doc (summaryUpdatedAt).
          // If not yet processed (new customer, trigger hasn't run), leave uncached
          // so the next watch trigger re-fetches with updated data.
          if (data.summaryUpdatedAt || data.nextActionable) {
            loadedIds.value.add(snap.id)
          }
        }
      })
      // Batch reassign to guarantee Vue reactivity triggers for all downstream computeds
      workflowsByCustomerId.value = updated
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Get the full customerWorkflows doc for a customer.
   */
  function getWorkflow(customerId) {
    return workflowsByCustomerId.value[customerId] || null
  }

  /**
   * Get the next actionable task entry from customerWorkflows.
   * Primary: top-level nextActionable field (written by updateWorkflowSummary backend).
   * Fallback: scan taskState entries for nextActionable flag (legacy backfill data).
   * Returns { taskId, ...entry } or null.
   */
  function getNextActionableTaskEntry(customerId) {
    const wf = getWorkflow(customerId)
    if (!wf) return null

    // V2: top-level nextActionable written by updateWorkflowSummary
    // This is the authoritative source — backend engine checks dependencies.
    if (wf.nextActionable?.taskId) {
      return { ...wf.nextActionable }
    }

    // If backend has already computed for this customer (summaryUpdatedAt exists),
    // trust its result: nextActionable is null → no actionable task (project complete/done).
    if (wf.summaryUpdatedAt) return null

    // Legacy fallback: scan taskState for backfill nextActionable flag.
    // Skip completed tasks — backfill flags are static and can become stale.
    if (wf.taskState) {
      for (const [instanceId, entry] of Object.entries(wf.taskState)) {
        if (entry.nextActionable && !entry.isComplete && entry.value !== true) {
          // entry.taskId is the masterTaskId — spread entry first, then override taskId
          // with the instanceId key so buildV2Task gets the correct runtime ID.
          return { ...entry, taskId: instanceId, masterTaskId: entry.taskId || instanceId }
        }
      }
    }
    return null
  }

  /**
   * Check if a specific task is complete.
   */
  function isTaskComplete(customerId, taskId) {
    const wf = getWorkflow(customerId)
    // taskId may be an instanceId (direct key) or a masterTaskId (from config)
    const entry = findTaskStateEntry(wf?.taskState, taskId)
    return entry?.isComplete === true
  }

  /**
   * Check if a specific task is rejected.
   */
  function isTaskRejected(customerId, taskId) {
    const wf = getWorkflow(customerId)
    const entry = findTaskStateEntry(wf?.taskState, taskId)
    return entry?.isRejected === true
  }

  /**
   * Check if a specific stage is complete.
   */
  function isStageComplete(customerId, stageId) {
    const wf = getWorkflow(customerId)
    return wf?.stageState?.[stageId]?.isComplete === true
  }

  /**
   * Get all completed stage IDs for a customer.
   */
  function getCompletedStageIds(customerId) {
    const wf = getWorkflow(customerId)
    if (!wf?.stageState) return []
    return Object.entries(wf.stageState)
      .filter(([_, s]) => s.isComplete)
      .map(([stageId]) => stageId)
  }

  /**
   * Get the manual task override for a customer.
   */
  function getManualOverride(customerId) {
    const wf = getWorkflow(customerId)
    return wf?.manualTaskOverride || null
  }

  /**
   * Get dismissed task IDs for a customer.
   */
  function getDismissedTasks(customerId) {
    const wf = getWorkflow(customerId)
    return wf?.dismissedTasks || []
  }

  /**
   * Load ALL customerWorkflows docs in a single query.
   * Used by admin pages (e.g., AdminFinancePage) that need cross-customer data.
   */
  async function loadAll(force = false) {
    // Guard on a dedicated whole-collection flag, NOT loadedIds — that Set is
    // shared with loadForCustomers/subscribeForCustomers, so "loaded some"
    // used to read as "loaded all" and admin surfaces (install forecast,
    // AdminFinancePage) computed over a partial workflow map
    // (bugReports/Zd7obFIy9gh8NUVWD27k).
    if (!force && allLoaded.value) return
    isLoading.value = true
    const sequence = reads.start()
    try {
      const snap = await getDocs(collection(db, 'customerWorkflows'))
      // Mirror `loadForCustomers`: accumulate into a local object then
      // reassign in one shot. Direct mutation of `workflowsByCustomerId.value[id]`
      // inside a forEach can fail to re-trigger downstream computeds in some
      // cases — admin pages that called `loadAll` (e.g. AdminFinancePage)
      // were occasionally seeing stale renders.
      const accumulated = { ...workflowsByCustomerId.value }
      snap.forEach(docSnap => {
        if (!reads.lands(docSnap.id, sequence)) return
        const data = docSnap.data()
        accumulated[docSnap.id] = { id: docSnap.id, ...data }
        loadedIds.value.add(docSnap.id)
      })
      workflowsByCustomerId.value = accumulated
      allLoaded.value = true
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Optimistic cache write-through for edit surfaces that mutate a task value
   * WITHOUT a realtime subscription (AR cockpit). After a successful Firestore
   * write, mirror the new value into the cached workflow so downstream
   * computeds re-derive immediately — otherwise the cache serves the stale
   * value until a full reload and the edited field appears wiped
   * (bugReports/x5v4dhtMeA10h71b4NL2). Reassign-don't-mutate, same rationale
   * as the loadAll note above.
   */
  function applyLocalTaskValue(customerId, instanceId, value) {
    const wf = workflowsByCustomerId.value[customerId]
    if (!wf) return
    const taskState = { ...(wf.taskState || {}) }
    taskState[instanceId] = { ...(taskState[instanceId] || {}), value }
    workflowsByCustomerId.value = {
      ...workflowsByCustomerId.value,
      [customerId]: { ...wf, taskState },
    }
  }

  // ─── Real-Time Subscriptions (for calendar page) ──────────────
  const activeSubscriptions = ref(new Map()) // customerId → unsubscribe fn

  /**
   * Subscribe to real-time updates for a batch of customer IDs.
   * Uses onSnapshot listeners so taskState changes appear immediately in the UI.
   * Skips customers that already have an active subscription.
   */
  function subscribeForCustomers(customerIds) {
    for (const id of customerIds) {
      if (activeSubscriptions.value.has(id)) continue
      const unsub = onSnapshot(doc(db, 'customerWorkflows', id), snap => {
        // A live snapshot is the newest read there is.
        if (snap.exists() && reads.lands(snap.id, reads.start())) {
          const data = snap.data()
          workflowsByCustomerId.value = {
            ...workflowsByCustomerId.value,
            [snap.id]: { id: snap.id, ...data },
          }
          loadedIds.value.add(snap.id)
        }
      })
      activeSubscriptions.value.set(id, unsub)
    }
  }

  /**
   * Unsubscribe all active real-time listeners.
   */
  function unsubscribeAll() {
    for (const unsub of activeSubscriptions.value.values()) unsub()
    activeSubscriptions.value.clear()
  }

  /**
   * Clear all cached data (e.g., on logout).
   */
  function clear() {
    unsubscribeAll()
    workflowsByCustomerId.value = {}
    loadedIds.value = new Set()
    allLoaded.value = false
    reads.clear()
  }

  return {
    workflowsByCustomerId,
    isLoading,
    loadedIds,
    loadForCustomers,
    subscribeForCustomers,
    unsubscribeAll,
    loadAll,
    getWorkflow,
    getNextActionableTaskEntry,
    isTaskComplete,
    isTaskRejected,
    isStageComplete,
    getCompletedStageIds,
    getManualOverride,
    getDismissedTasks,
    applyLocalTaskValue,
    clear,
  }
})
